<template>
  <v-dialog :value="openDialog" scrollable width="805px" @input="$emit('input', $event)" @click:outside="closeDialog">
    <v-card v-if="localProduct" class="product-edit" height="560px">
      <v-card-title class="px-8">
        <span class="section-title">{{ isNew ? 'Criar produto' : 'Editar produto' }}</span> <br />
        <p class="subtitle pt-1" style="word-break: break-word">
          {{
            isNew
              ? 'Produtos criados aqui precisam estar cadastrados no seu ERP/PDV para serem utilizados efetivamente numa campanha de ofertas. '
              : 'Edições feitas serão aplicadas em todos os lugares que consomem produtos, como por exemplo seu frente de caixa e aplicativo.'
          }}
        </p>
      </v-card-title>
      <v-card-text class="card-content px-8">
        <v-container fluid class="pa-0">
          <v-row class="pa-0">
            <v-col cols="5">
              <div class="d-flex align-center">
                <mf-upload-image v-model="localProduct.image" fileName="product" @upload="saveImage" />
              </div>
            </v-col>
            <v-col cols="7">
              <v-row>
                <v-col cols="4" class="pb-0 pt-0">
                  <v-text-field
                    v-model="localProduct.sku"
                    class="hidden-arrows"
                    :disabled="!isNew"
                    :error-messages="SKUErrorMessage"
                    label="SKU"
                    outlined
                    height="45px"
                    data-cy="productSkuField"
                    type="number"
                    @blur="validateSku"
                    @focus="newSKUError = ''"
                    @keydown="onSkuKeydown"
                  />
                </v-col>
                <v-col cols="8" class="pb-0 pt-0">
                  <v-text-field
                    v-model="localProduct.name"
                    label="Descrição"
                    height="45px"
                    :error-messages="nameErrorMessage"
                    outlined
                    data-cy="productDescriptionField"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="8" class="py-0 pr-1">
                  <v-text-field
                    v-model="newEan"
                    v-mask="'##############'"
                    label="EAN"
                    :error-messages="eansErrorMessage"
                    :hide-details="!Boolean(eansErrorMessage)"
                    outlined
                    data-cy="productEanField"
                    @keydown.enter="addEan"
                    @focus="newEANError = ''"
                  />
                </v-col>
                <v-col cols="4" class="py-0 pl-3">
                  <mf-button
                    :disabled="newEan === '' || validatingSKU"
                    size="lg"
                    block
                    :loading="validatingEAN"
                    outlined
                    data-cy="productAddEanButton"
                    label="Adicionar"
                    @click="addEan"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col class="pt-0">
                  <v-chip-group show-arrows active-class="primary--text" :column="showAllEans">
                    <v-chip v-for="(ean, index) in localEans" :key="index" small close :ripple="false" @click:close="removeEan(index)">
                      <span class="column-title">
                        {{ ean }}
                      </span>
                    </v-chip>
                  </v-chip-group>
                  <span v-if="localEans && localEans.length" class="link" @click="showAllEans = !showAllEans">{{
                    showAllEans ? 'Mostrar menos' : 'Mostrar tudo'
                  }}</span>
                </v-col>
              </v-row>
              <v-row v-if="!$getAllowedModules()['products_category']">
                <v-col cols="12" class="py-2">
                  <v-select
                    v-model="localProduct.category"
                    :items="orderBy(categories, 'name')"
                    label="Categoria"
                    hide-details
                    height="45px"
                    outlined
                    return-object
                    item-text="name"
                  />
                </v-col>
              </v-row>
              <v-row v-else>
                <v-col cols="12" class="pt-2 pb-3">
                  <span class="category-label">Categorias</span>
                  <v-select
                    v-model="localProduct.categories_final_consumer.department"
                    class="pt-3"
                    :items="orderBy(categoriesTypes[0], 'name')"
                    label="Categoria nível 1"
                    height="45px"
                    outlined
                    hide-details
                    return-object
                    item-text="name"
                    @change="changeDepartments"
                  />
                </v-col>
                <v-col cols="12" class="py-0" :class="{ 'pb-3': !categoryErrorMessage }">
                  <v-select
                    v-model="localProduct.categories_final_consumer.category"
                    :items="orderBy(categoriesWithDepartment, 'name')"
                    label="Categoria nível 2"
                    height="45px"
                    outlined
                    :disabled="
                      !localProduct.categories_final_consumer.department || Object.keys(localProduct.categories_final_consumer.department).length === 0
                    "
                    return-object
                    item-text="name"
                    :error-messages="categoryErrorMessage"
                    :error="showCategoriesValidation ? $v.localProduct.categories_final_consumer.category.$error : false"
                    :hide-details="!categoryErrorMessage"
                    @change="changeCategory"
                  />
                </v-col>
                <v-col cols="12" class="py-0" :class="{ 'pb-3': !subcategoryErrorMessage }">
                  <v-select
                    v-model="localProduct.categories_final_consumer.sub_category"
                    :items="orderBy(subCategoriesWithCategory, 'name')"
                    label="Categoria nível 3"
                    height="45px"
                    :disabled="!localProduct.categories_final_consumer.category || Object.keys(localProduct.categories_final_consumer.category).length === 0"
                    outlined
                    return-object
                    :error-messages="subcategoryErrorMessage"
                    :error="showCategoriesValidation && thirdCategoryRequired ? $v.localProduct.categories_final_consumer.sub_category.$error : false"
                    :hide-details="!subcategoryErrorMessage"
                    item-text="name"
                  />
                </v-col>
                <span v-if="showCategoriesValidation" class="clear-categories ml-3 mb-4" @click="clearCategories">Limpar categorias</span>
              </v-row>
              <v-row>
                <v-col cols="6" class="py-2">
                  <v-select
                    v-model="localProduct.type"
                    :items="[
                      { id: 1, text: 'Unidade', value: 'UNIT' },
                      { id: 2, text: 'Pesável', value: 'WEIGHT' },
                    ]"
                    label="Unidade de medida"
                    hide-details
                    height="45px"
                    outlined
                    item-value="value"
                    item-text="text"
                  />
                </v-col>
                <v-col cols="6" class="py-2">
                  <v-row>
                    <v-col class="py-0">
                      <v-text-field
                        v-model.lazy="basisWeightKg"
                        v-money="kgFormat"
                        :disabled="localProduct.type !== 'WEIGHT'"
                        placeholder="0,00"
                        suffix="Kg"
                        label="Peso base"
                        :error-messages="basisWeightErrorMessage"
                        :hide-details="!Boolean(basisWeightErrorMessage)"
                        outlined
                        @focus="errorWeight = false"
                        @keydown="$event.key === '-' ? $event.preventDefault() : null"
                      >
                        <template v-slot:append-outer>
                          <v-tooltip right max-width="200px" :disabled="localProduct.type !== 'WEIGHT'">
                            <template v-slot:activator="{ on }">
                              <span v-on="on">
                                <mf-icon small color="cinza-0">info</mf-icon>
                              </span>
                            </template>
                            <span>Representa o peso de uma unidade selecionada pelo consumidor</span>
                          </v-tooltip>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col class="py-0">
                      <span class="texto-corrido-pequeno pl-1"> Ex: 0,20kg representam 200 gramas </span>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
        <mf-action-buttons
          :primary-button="{
            text: 'Salvar',
            action: submit,
            isVisible: true,
            isLoading: loading || loadingImageUpload,
            isDisabled: validatingSKU || validatingEAN,
          }"
          :extra-button="{
            text: 'Cancelar',
            action: closeDialog || loadingImageUpload,
            isVisible: true,
          }"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { orderBy, cloneDeep } from 'lodash'
import { required, minLength, maxLength } from 'vuelidate/lib/validators'
import { notOnlySpace } from '@/helpers/validators.js'
import { UPDATE_PRODUCT, CREATE_PRODUCT, PRODUCT } from '../../../graphql'
import { VMoney } from 'v-money'
import { MUTATION_SINGLE_UPLOAD_BASE64 } from '@/graphql'

export default {
  directives: { money: VMoney },
  props: {
    dialog: Boolean,
    hasNotDepartment: Boolean,
    hasNotCategory: Boolean,
    product: {
      type: Object,
      default: () => {},
    },
    categories: Array,
    categoriesTypes: Array,
    isNew: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    file: undefined,
    filePreview: null,
    localProduct: {},
    localEans: [],
    loading: false,
    orderBy: orderBy,
    basisWeightKg: '',
    newEan: '',
    showAllEans: false,
    validatingEAN: false,
    validatingSKU: false,
    newSKUError: '',
    newEANError: '',
    kgFormat: {
      decimal: ',',
      thousands: '.',
      precision: 2,
      masked: false,
    },
    errorWeight: false,
    categoriesWithDepartment: [],
    subCategoriesWithCategory: [],
    loadingImageUpload: false,
  }),
  computed: {
    openDialog: {
      get() {
        return this.dialog
      },
      set(value) {
        this.$emit('update:dialog', value)
      },
    },
    SKUErrorMessage() {
      if (this.newSKUError) return this.newSKUError
      if (this.$v.localProduct.sku.$error && !this.$v.localProduct.sku.required) return 'Campo obrigatório'
      if (this.$v.localProduct.sku.$error && !this.$v.localProduct.sku.notOnlySpace) return 'Não é permitido salvar apenas espaços'
      return ''
    },
    nameErrorMessage() {
      if (this.$v.localProduct.name.$error && !this.$v.localProduct.name.required) return 'Campo obrigatório'
      if (this.$v.localProduct.name.$error && !this.$v.localProduct.name.notOnlySpace) return 'Não é permitido salvar apenas espaços'
      if (this.$v.localProduct.name.$error && !this.$v.localProduct.name.minLength) return 'O campo deve ter no mínimo 3 letras'
      if (this.$v.localProduct.name.$error && !this.$v.localProduct.name.maxLength) return 'O campo deve ter no máximo 100 letras'
      return ''
    },
    categoryErrorMessage() {
      if (!this.showCategoriesValidation) return ''
      if (this.$v.localProduct?.categories_final_consumer?.category?.$error) return 'É necessário selecionar todos os níveis de categorias para avançar.'
      return ''
    },
    subcategoryErrorMessage() {
      if (!this.thirdCategoryRequired) return ''
      if (this.$v.localProduct?.categories_final_consumer?.sub_category?.$error) return 'É necessário selecionar todos os níveis de categorias para avançar.'
      return ''
    },
    eansErrorMessage() {
      if (this.newEANError) return this.newEANError
      if (this.$v.localEans.$error && !this.$v.localEans.required) return 'Campo obrigatório'
      return ''
    },
    basisWeightErrorMessage() {
      if (this.localProduct.type === 'WEIGHT' && this.errorWeight) {
        return 'Campo obrigatório'
      }
      return ''
    },
    showCategoriesValidation() {
      return !!this.localProduct?.categories_final_consumer?.department?._id
    },
    thirdCategoryRequired() {
      return !!this.localProduct?.categories_final_consumer?.category?._id
    },
  },
  created() {
    this.openDialog = true
    this.setData()
    this.categoriesChosen()
  },
  methods: {
    onSkuKeydown(event) {
      if (['-', 'e', '.'].includes(event.key)) {
        event.preventDefault()
      }
    },
    setData() {
      this.$v.$reset()
      this.newEANError = ''
      this.newSKUError = ''
      this.newEan = ''
      this.localProduct = cloneDeep(this.product)
      this.showAllEans = false
      this.errorWeight = false
      // Set eans as array when creating new products
      if (this.localProduct?.eans) this.localEans = this.localProduct.eans
      else this.localEans = []
      this.basisWeightKg = this.localProduct?.basis_weight ? this.localProduct.basis_weight.toFixed(2).replace('.', ',') : '0,00'
    },
    async validateSku() {
      if (!this.localProduct.sku) return

      try {
        this.validatingSKU = true
        const {
          data: { product },
        } = await this.$apollo.query({
          query: PRODUCT,
          variables: { sku: this.localProduct.sku },
          fetchPolicy: 'network-only',
        })
        if (product) {
          this.newSKUError = 'SKU já cadastrada'
        } else {
          this.newSKUError = ''
        }
      } catch (e) {
        console.log(e)
        this.$snackbar({ message: 'Falha ao validar o SKU no servidor.' })
      }
      this.validatingSKU = false
    },
    categoriesChosen() {
      if (this.localProduct?.categories_final_consumer?.department && Object.keys(this.localProduct?.categories_final_consumer?.department).length > 0) {
        this.categoriesWithDepartment = this.categoriesTypes[1].filter(
          (i) => i.department_id === this.localProduct.categories_final_consumer.department._id.toString()
        )
      }
      if (this.localProduct?.categories_final_consumer?.department && Object.keys(this.localProduct.categories_final_consumer.category).length > 0) {
        this.subCategoriesWithCategory = this.categoriesTypes[2].filter(
          (i) => i.category_id === this.localProduct.categories_final_consumer.category._id.toString()
        )
      }
    },
    changeDepartments() {
      if (Object.keys(this.localProduct.categories_final_consumer.department).length > 0) {
        this.categoriesWithDepartment = this.categoriesTypes[1].filter(
          (i) => i.department_id === this.localProduct.categories_final_consumer.department._id.toString()
        )
      }
      this.localProduct.categories_final_consumer.category = []
      this.localProduct.categories_final_consumer.sub_category = []
    },
    changeCategory() {
      if (Object.keys(this.localProduct.categories_final_consumer.category).length > 0) {
        this.subCategoriesWithCategory = this.categoriesTypes[2].filter(
          (i) => i.category_id === this.localProduct.categories_final_consumer.category._id.toString()
        )
      }
      this.localProduct.categories_final_consumer.sub_category = []
    },
    async addEan() {
      // Block multiple calls
      if (this.validatingEAN) return

      // Check the product EANs locally
      if (this.localEans.some((ean) => ean === this.newEan)) {
        this.newEANError = 'EAN já adicionado'
        return
      }

      // Check if any other product already exists with this EAN
      try {
        this.validatingEAN = true
        const {
          data: { product },
        } = await this.$apollo.query({
          query: PRODUCT,
          variables: { ean: this.newEan },
          fetchPolicy: 'network-only',
        })
        // Either no product is found, or the product being edited is found
        if (!product || (product.sku === this.localProduct.sku && this.SKUErrorMessage === '')) {
          this.localEans.push(this.newEan)
          this.newEan = ''
        } else {
          this.newEANError = 'EAN já cadastrado em outro produto'
        }
      } catch (e) {
        console.log(e)
        this.$snackbar({ message: 'Falha ao validar o EAN no servidor.' })
      }
      this.validatingEAN = false
    },
    removeEan(index) {
      const eans = cloneDeep(this.localEans)
      eans.splice(index, 1)
      this.localEans = eans
    },
    closeDialog() {
      this.localProduct = {
        categories_final_consumer: {
          department: {},
          category: {},
          sub_category: {},
        },
      }
      this.file = undefined
      this.filePreview = null
      this.openDialog = false
      this.localProduct.image = ''
      this.$emit('input', false)
    },
    async updateProduct() {
      let consumerCategories = {}

      if (this.$getAllowedModules()['products_category']) {
        this.localProduct.category = null
        if (this.localProduct.categories_final_consumer.department !== null) {
          consumerCategories = {
            department_id: this.localProduct.categories_final_consumer?.department._id || null,
            category_id: this.localProduct.categories_final_consumer?.category._id || null,
            sub_category_id: this.localProduct.categories_final_consumer?.sub_category._id || null,
          }
        }
      } else {
        this.localProduct.categories_final_consumer = null
      }

      let categories_final_consumer = null

      if (this.localProduct?.categories_final_consumer?.department) {
        categories_final_consumer = Object.keys(this.localProduct.categories_final_consumer.department).length > 0 ? consumerCategories : null
      }

      const variables = {
        _id: this.localProduct._id,
        name: this.localProduct.name,
        category: this.localProduct?.category?._id || null,
        categories_final_consumer,
        eans: this.localEans,
        image_url: this.localProduct.image || '',
        type: this.localProduct.type,
        basis_weight: this.localProduct.basis_weight || null,
      }

      await this.$apollo.mutate({
        mutation: UPDATE_PRODUCT,
        variables: { data: variables },
      })
    },
    async saveImage({ base64, filename }) {
      this.loadingImageUpload = true
      await this.$apollo
        .mutate({
          mutation: MUTATION_SINGLE_UPLOAD_BASE64,
          variables: { base64, filename },
          context: {
            uri: this.$microservicesUrls['upload'],
          },
        })
        .then(({ data: { singleUploadBase64 } }) => {
          const { url } = singleUploadBase64
          this.localProduct.image = url
          this.loadingImageUpload = false
        })
        .catch((error) => {
          console.log(error)
          this.loadingImageUpload = false
          this.$snackbar({ message: 'Erro ao subir imagem.', snackbarColor: 'error' })
        })
    },
    async createProduct() {
      const consumerCategories = {
        department_id: this.localProduct?.categories_final_consumer?.department?._id || null,
        category_id: this.localProduct?.categories_final_consumer?.category?._id || null,
        sub_category_id: this.localProduct?.categories_final_consumer?.sub_category?._id || null,
      }

      const variables = {
        sku: this.localProduct.sku,
        name: this.localProduct.name,
        category: this.localProduct?.category?._id || null,
        categories_final_consumer: Object.keys(this.localProduct.categories_final_consumer.department).length > 0 ? consumerCategories : null,
        eans: this.localEans,
        image_url: this.localProduct.image || '',
        type: this.localProduct.type,
        basis_weight: this.localProduct.basis_weight || null,
      }

      await this.$apollo.mutate({
        mutation: CREATE_PRODUCT,
        variables: { data: variables },
      })
    },
    async submit() {
      this.localProduct.basis_weight = this.localProduct.type === 'WEIGHT' ? Number(this.basisWeightKg.replace(/\./g, '').replace(',', '.')) : null
      this.$v.$touch()
      if (this.localProduct.type === 'WEIGHT' && this.localProduct.basis_weight <= 0) {
        this.errorWeight = true
      } else {
        this.errorWeight = false
      }

      if (this.$v.$error || this.errorWeight || this.newSKUError) {
        return this.$snackbar({ message: 'Preencha todos os campos corretamente', snackbarColor: 'error' })
      }

      try {
        this.loading = true

        if (this.isNew) {
          await this.createProduct()
        } else {
          await this.updateProduct()
        }
        this.$emit('updated')
        if (this.isNew) this.$snackbar({ message: 'Produto criado com sucesso!', snackbarColor: 'success' })
        else this.$snackbar({ message: 'Produto atualizado com sucesso!', snackbarColor: 'success' })
      } catch (error) {
        console.log(error)
        if (this.isNew) this.$snackbar({ message: 'Erro ao criar produto', snackbarColor: 'error' })
        else this.$snackbar({ message: 'Erro ao editar produto', snackbarColor: 'error' })
      } finally {
        this.loading = false
        this.closeDialog()
      }
    },
    clearCategories() {
      this.localProduct.categories_final_consumer.department = {}
      this.localProduct.categories_final_consumer.category = {}
      this.localProduct.categories_final_consumer.sub_category = {}
    },
  },
  validations() {
    const categories_final_consumer = {
      ...(this.showCategoriesValidation && { category: { required } }),
      ...(this.thirdCategoryRequired && { sub_category: { required } }),
    }

    return {
      localProduct: {
        sku: { required, notOnlySpace },
        name: { required, notOnlySpace, minLength: minLength(3), maxLength: maxLength(100) },
        ...(this.showCategoriesValidation && { categories_final_consumer }),
      },
      localEans: {
        required,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.product-edit {
  .image-placeholder {
    background-color: $cinza-l90;
    width: 230px;
    height: 230px;
    &:hover {
      cursor: pointer;
    }
    border: solid 1px $cinza-l10;
    text-align: center;
    display: table;
  }
  .category-label {
    font-family: 'Lato';
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 120%;
    color: $cinza-d10;
  }
  .centered-text {
    display: table-cell;
    vertical-align: middle;
  }
  .product-title {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 0;
    color: $cinza-d10;
  }
  .product-data {
    font-size: 16px;
    opacity: 0.87;
    margin-bottom: 0;
  }
  .input-file {
    display: none;
  }
  .hint-text {
    font-size: 12px;
    letter-spacing: 0.23px;
    color: $cinza-0;
  }
  .sub-text {
    font-size: 12px;
    letter-spacing: 0.23px;
  }
  .sub-text-disabled {
    font-size: 12px;
    letter-spacing: 0.23px;
    color: rgba(0, 0, 0, 0.38);
  }

  .clear-categories {
    color: $vermelho-0;
    &:hover {
      text-decoration: underline;
    }
    cursor: pointer;
  }
}
::v-deep .v-input__slot {
  min-height: 45px !important;
}
::v-deep .v-input__slot .v-text-field__slot .v-label {
  top: 14px !important;
}
::v-deep .v-input__slot .v-select__slot .v-label {
  top: 14px !important;
}
::v-deep .theme--light.v-text-field--outlined:not(.v-input--is-focused).v-input--is-disabled > .v-input__control > .v-input__slot fieldset {
  background: $cinza-l90 !important;
}
::v-deep .v-input__slot .v-select__slot .mf-icon {
  bottom: 4px !important;
}

.hidden-arrows {
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none !important;
    margin: 0 !important;
  }
  input[type='number'] {
    -moz-appearance: textfield !important;
  }
}
</style>
